import { AuthRole } from '~/utils/auth'
import {
  expertCommitteeNavigationRoles,
  expertCommitteeRoutes,
} from '~/utils/expertCommitteeRoutes'
import { useEnvironment } from '~/composables/use-environment'
import { Environment, type EnvironmentHostname } from '~/types/Environments'
import { resolvePortalUrl } from '~/utils/portal-url'
interface Route {
  name?: string
  id?: string
  path: string | { path: string; query: Record<string, string> }
  icon?: string
  auth?: {
    roles: AuthRole[]
    requiresAdminGroups?: boolean
  }
  children?: Route[]
  target?: string
  bold?: boolean
  disabled?: boolean
}

export const HubRoutes: Route[] = [
  {
    name: 'dashboard',
    path: '/hub/dashboard',
    icon: 'ic:outline-dashboard',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'management',
    path: '/hub/management',
    icon: 'ic:outline-build',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
    children: [
      { name: 'products', path: '/hub/management/products' },
      {
        name: 'productGroups',
        path: '/hub/management/product-groups',
      },
      { name: 'hazards', path: '/hub/management/hazards' },
      { name: 'operations', path: '/hub/management/operations' },
      {
        name: 'operationGroups',
        path: '/hub/management/operation-groups',
      },
      { name: 'controlMeasures', path: '/hub/management/control-measures' },
      {
        name: 'controlMeasureGroups',
        path: '/hub/management/control-measure-groups',
      },
      { name: 'processingAids', path: '/hub/management/processing-aids' },
      {
        name: 'processingAidGroups',
        path: '/hub/management/processing-aid-groups',
      },
      { name: 'processSteps', path: '/hub/management/process-steps' },
      {
        name: 'processStepGroups',
        path: '/hub/management/process-step-groups',
      },
      {
        name: 'monitoringGroups',
        path: '/hub/management/monitoring-groups',
      },
      { name: 'countries', path: '/hub/management/countries' },
      { name: 'scopes', path: '/hub/management/scopes' },
      { name: 'standards', path: '/hub/management/standards' },
      { name: 'scopesStandards', path: '/hub/management/scopes-standards' },
      { name: 'methods', path: '/hub/management/methods' },
      { name: 'units', path: '/hub/management/units' },
    ],
  },
  {
    name: 'riskAssessments',
    path: '/hub/risk-assessments',
    icon: 'custom:risk-assessments',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'locations',
    path: '/hub/locations',
    icon: 'mdi:company',
    auth: { roles: [AuthRole.QRAY_GMP] },
  },
  {
    name: 'laboratoryOperations',
    path: '/hub/laboratory-operations',
    icon: 'custom:laboratory',
    auth: { roles: [AuthRole.QRAY_GMP] },
  },
  {
    name: 'users',
    path: '/hub/users',
    icon: 'mdi:account-group',
    auth: { roles: [AuthRole.QRAY_GMP] },
  },
  {
    name: 'documents',
    path: '/hub/documents',
    icon: 'mdi:file-document-outline',
    auth: {
      roles: [AuthRole.UNRELEASED],
    },
  },
  {
    name: 'monitoring',
    path: '/hub/monitoring',
    icon: 'ic:outline-traffic',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'reports',
    path: '/hub/reports',
    icon: 'mdi:file-report-outline',
    auth: { roles: [AuthRole.QRAY_GMP] },
  },
  {
    name: 'incomingEmails',
    path: '/hub/incoming-emails',
    icon: 'ic:baseline-mail-outline',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'cmsCategories',
    path: '/hub/cms-categories',
    icon: 'ic:outline-category',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'searchIndices',
    path: '/hub/search-indices',
    icon: 'ic:outline-manage-search',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'sitemap',
    path: '/hub/sitemap',
    icon: 'mdi:sitemap-outline',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
]

export const PortalRoutes = (
  t: (str: string) => string,
  environment: EnvironmentHostname = useEnvironment().environment.value,
): Route[] => [
  {
    name: 'dashboard',
    path: '/portal/dashboard',
    icon: 'ic:outline-dashboard',
  },
  {
    id: 'menu-favourites',
    name: 'favourites',
    path: '/portal/favourites',
    icon: 'ic:round-star',
  },
  {
    id: 'menu-feed-scheme-documents',
    name: 'feedSchemeDocuments',
    path: '/portal/feed-scheme-documents',
    icon: 'ic:grain',
  },
  ...(environment === Environment.test || environment === Environment.production
    ? []
    : [
        {
          id: 'menu-feed-legislation',
          name: 'feedLegislation',
          path: '/portal/feed-legislation',
          icon: 'ic:outline-gavel',
          auth: { roles: [AuthRole.QRAY_GMP] },
        },
      ]),
  {
    id: 'menu-risk-management',
    name: 'riskManagementTools',
    path: '/portal/risk-management',
    icon: 'ic:outline-traffic',
    auth: {
      roles: [AuthRole.QRAY_GMP, AuthRole.QRAY_FSP, AuthRole.QRAY_MON, AuthRole.PORTAL_COMPANY],
    },
    children: [
      {
        name: 'riskAssessments',
        path: '/portal/risk-management/risk-assessments',
      },
      {
        name: 'acceptedFeedMaterials',
        path: '/portal/risk-management/accepted-feed-materials',
      },
      {
        name: 'processingAids',
        path: '/portal/risk-management/processing-aids',
      },
      {
        name: 'undesirableSubstances',
        path: '/portal/risk-management/undesirable-substances',
      },
      {
        name: 'monitoringDatabase',
        path: '/portal/risk-management/monitoring-database',
        auth: {
          roles: [
            AuthRole.QRAY_GMP,
            AuthRole.QRAY_MON,
            AuthRole.QRAY_GROUPMANAGER,
            AuthRole.PORTAL_COMPANY,
          ],
        },
        children: [
          {
            name: 'myMonitoring',
            path: {
              path: '/portal/risk-management/monitoring-database/my-monitoring',
              query: {
                levels: 'own',
              },
            },
          },
          {
            name: 'myGroupMonitoring',
            path: '/portal/risk-management/monitoring-database/my-group-monitoring',
            auth: {
              roles: [AuthRole.QRAY_GMP],
              requiresAdminGroups: true,
            },
          },
          {
            name: 'myProducts',
            path: '/portal/risk-management/monitoring-database/my-products',
          },
          {
            name: 'analysisSets',
            path: '/portal/risk-management/monitoring-database/analysis-sets',
            disabled: false,
          },
          {
            name: 'myGroups',
            path: '/portal/risk-management/monitoring-database/my-groups',
            disabled: false,
          },
          {
            name: 'groupManagement',
            path: '/portal/risk-management/monitoring-database/management-group-members',
            disabled: false,
            auth: {
              roles: [AuthRole.QRAY_GMP, AuthRole.QRAY_GROUPMANAGER],
              requiresAdminGroups: true,
            },
          },
          {
            name: 'reports',
            path: '/portal/risk-management/monitoring-database/reports',
            disabled: false,
            auth: {
              roles: [AuthRole.UNRELEASED],
            },
          },
          {
            name: 'codeLists',
            path: '/portal/risk-management/monitoring-database/code-lists',
          },
        ],
      },
    ],
  },
  {
    name: 'gmpCompanyDatabase',
    icon: 'ic:baseline-domain',
    path: '/portal/certified-companies',
  },
  {
    name: 'earlyWarningSystem',
    path: resolvePortalUrl(t('externalLinks.earlyWarningSystem'), environment),
    icon: 'ic:baseline-warning-amber',
    target: '_blank',
    auth: {
      roles: [AuthRole.PORTAL_COMPANY],
    },
  },
  {
    name: 'ewsNew',
    path: '/portal/ews',
    icon: 'material-symbols:pending-actions',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'cbLogin',
    path: resolvePortalUrl(t('externalLinks.cbLogin'), environment),
    icon: 'ic:baseline-card-travel',
    target: '_blank',
    auth: {
      roles: [
        AuthRole.PORTAL_EWS,
        AuthRole.PORTAL_ADM,
        AuthRole.PORTAL_COORDINATOR,
        AuthRole.PORTAL_SCAC,
        AuthRole.PORTAL_TCFSP,
        AuthRole.PORTAL_STAC,
        AuthRole.PORTAL_ST,
        AuthRole.PORTAL_SRF,
        AuthRole.PORTAL_SP,
        AuthRole.PORTAL_IECAF,
      ],
    },
  },
  {
    name: 'exemptions',
    path: resolvePortalUrl(t('externalLinks.exemptions'), environment),
    icon: 'ic:outline-folder-special',
    target: '_blank',
    auth: {
      roles: [AuthRole.PORTAL_EXEMPTION, AuthRole.PORTAL_COMPANY],
    },
  },
  {
    name: 'exemptionsNew',
    path: '/portal/exemptions',
    icon: 'material-symbols:pending-actions',
    auth: {
      roles: [AuthRole.QRAY_GMP],
    },
  },
  {
    name: 'helpdesk',
    path: '/portal/helpdesk',
    icon: 'material-symbols:help-outline',
  },
  {
    name: 'riskAssessmentSubmissions',
    path: '/portal/risk-assessments',
    icon: 'ic:traffic',
  },
  {
    id: 'menu-my-downloads',
    name: 'myDownloads',
    path: '/portal/exports',
    icon: 'material-symbols:download',
    auth: { roles: [AuthRole.QRAY_GMP, AuthRole.QRAY_MON, AuthRole.PORTAL_COMPANY] },
  },
  {
    path: '#',
    id: 'menu-unreleased',
    name: 'unreleased',
    icon: 'ic:outline-logo-dev',
    auth: {
      roles: expertCommitteeNavigationRoles,
    },
    children: [
      {
        name: 'uploadNewSample',
        path: '/portal/risk-management/monitoring-database/upload-new-sample',
        icon: 'material-symbols:upload-file-outline',
        auth: {
          roles: [AuthRole.QRAY_GMP],
        },
      },
      {
        name: 'companyDbExport',
        path: '/portal/company-db-export',
        icon: 'ic:baseline-download',
        auth: {
          roles: [AuthRole.QRAY_GMP],
        },
      },
      {
        name: 'virtualAssistant',
        path: '/portal/chat',
        icon: 'material-symbols:chat-outline',
        auth: {
          roles: [AuthRole.QRAY_GMP],
        },
      },
      {
        name: 'expertCommittee',
        path: '#',
        icon: 'ic:outline-groups',
        auth: {
          roles: expertCommitteeNavigationRoles,
        },
        bold: true,
        children: expertCommitteeRoutes.map(({ navigationName, roles, slug }) => ({
          name: navigationName,
          path: `/portal/expert-committees/${slug}`,
          auth: {
            roles: [...roles],
          },
        })),
      },
    ],
  },
]

export function updateNestedRouteQuery(
  routes: Route[],
  pathSegments: string[],
  newQuery: Record<string, string>,
): Route[] {
  const routesCopy: Route[] = JSON.parse(JSON.stringify(routes))

  let currentLevel = routesCopy
  let targetRoute: Route | undefined

  for (const segment of pathSegments) {
    targetRoute = currentLevel.find((route) => {
      if (typeof route.path === 'string') {
        return route.path === segment
      }
      return route.path.path === segment
    })

    if (!targetRoute) {
      return routes
    }

    if (targetRoute.children) {
      currentLevel = targetRoute.children
    }
  }

  if (targetRoute) {
    if (typeof targetRoute.path === 'string') {
      targetRoute.path = {
        path: targetRoute.path,
        query: newQuery,
      }
    } else {
      targetRoute.path.query = {
        ...targetRoute.path.query,
        ...newQuery,
      }
    }
  }

  return routesCopy
}
